import React, { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Chart } from 'react-google-charts';
import './weekchart.scss';
import { useHandleError } from 'shared-pages/services';
import { useWhyDidYouUpdate } from 'shared-pages';
import { useTranslation } from 'react-i18next';

export const WeekRep = () => {
  const { handleGraphqlError } = useHandleError('WeekRep');
  const { t, i18n } = useTranslation();

  const {
    data: rawData,
    loading,
    error,
  } = useQuery(
    gql`
      query getCollectionsChart {
        getCollectionsChart {
          datatype
          today
          prvDay1
          prvDay2
          prvDay3
          prvDay4
          prvDay5
          prvDay6
          prvDay7
        }
      }
    `,
    {
      fetchPolicy: 'network-only',
      onError: (e) => handleGraphqlError('WeekRep', e),
    },
  );

  const rows = rawData?.getCollectionsChart;
  useWhyDidYouUpdate('[WeekRep]', { rows, loading, error });

  const n = (v: unknown) =>
    typeof v === 'number' ? v : Number.parseInt((v as string) ?? '0', 10) || 0;

  // Safer mapping: prefer datatype labels; fall back to positional indices
  const { dates, reqs, cols } = useMemo(() => {
    const byType = new Map<string, any>();
    if (Array.isArray(rows)) {
      for (const r of rows) {
        if (r?.datatype) byType.set(String(r.datatype).toLowerCase(), r);
      }
    }
    const d0 = byType.get('date') ?? rows?.[0] ?? {};
    const d1 = byType.get('requests') ?? rows?.[1] ?? {};
    const d2 = byType.get('collections') ?? rows?.[2] ?? {};
    return { dates: d0, reqs: d1, cols: d2 };
  }, [rows]);

  const ready = dates && reqs && cols && ['prvDay1', 'prvDay7', 'today'].every((k) => k in dates);

  const chartData = useMemo(() => {
    const L_DATE = i18n.language === 'gr' ? 'Ημερομηνία' : 'Date';
    const L_REQS = i18n.language === 'gr' ? 'Αιτήσεις Συλλογής' : 'Collection Requests';
    const L_COLS = i18n.language === 'gr' ? 'Συλλογές' : 'Collections';
    const L_TODAY = i18n.language === 'gr' ? 'Σήμερα' : 'Today';

    if (!ready) {
      return [
        [L_DATE, L_REQS, L_COLS],
        ['Loading...', 0, 0],
      ] as (string | number)[][];
    }
    return [
      [L_DATE, L_REQS, L_COLS],
      [dates.prvDay7 ?? '', n(reqs.prvDay7), n(cols.prvDay7)],
      [dates.prvDay6 ?? '', n(reqs.prvDay6), n(cols.prvDay6)],
      [dates.prvDay5 ?? '', n(reqs.prvDay5), n(cols.prvDay5)],
      [dates.prvDay4 ?? '', n(reqs.prvDay4), n(cols.prvDay4)],
      [dates.prvDay3 ?? '', n(reqs.prvDay3), n(cols.prvDay3)],
      [dates.prvDay2 ?? '', n(reqs.prvDay2), n(cols.prvDay2)],
      [dates.prvDay1 ?? '', n(reqs.prvDay1), n(cols.prvDay1)],
      [L_TODAY, n(reqs.today), n(cols.today)],
    ];
  }, [ready, dates, reqs, cols, i18n.language]);

  const options = useMemo(
    () => ({
      hAxis: {
        textStyle: { color: '#6c757d', fontSize: 10 },
        slantedText: true,
        slantedTextAngle: 45,
      },
      vAxis: { minValue: 0, textStyle: { color: '#6c757d', fontSize: 10 } },
      pointsVisible: true,
      pointSize: 3,
      isStacked: false,
      legend: {
        position: 'bottom',
        alignment: 'center',
        textStyle: { color: '#343a40', fontSize: 11 },
      },
      chartArea: {
        left: '10%',
        top: '5%',
        width: '85%',
        height: '75%',
        backgroundColor: 'transparent',
      },
      colors: ['#228B22', '#90EE90'],
      backgroundColor: 'transparent',
      lineWidth: 2,
      areaOpacity: 0.15,
      focusTarget: 'category',
      crosshair: { trigger: 'both', color: '#dee2e6' },
      tooltip: { textStyle: { fontSize: 12 } },
    }),
    [],
  );

  if (error) {
    return (
      <div className="fk-chart-container">
        <h3 className="fk-chart-title">
          {i18n.language === 'gr' ? 'Εβδομαδιαία Ανάλυση Συλλογών' : 'Weekly Collection Analysis'}
        </h3>
        <div className="text-center p-4">
          <div className="alert alert-light border" style={{ color: 'var(--fk-gray-600)' }}>
            <i className="bi bi-exclamation-triangle me-2" />
            {i18n.language === 'gr'
              ? 'Δεν είναι διαθέσιμα δεδομένα γραφήματος αυτή τη στιγμή'
              : 'Chart data is not available at the moment'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <h3 className="fk-chart-title">
        {i18n.language === 'gr' ? 'Εβδομαδιαία Ανάλυση Συλλογών' : 'Weekly Collection Analysis'}
      </h3>
      {!loading ? (
        <div
          className="weekchart"
          style={{
            width: '100%',
            height: 'clamp(250px, 50vh, 400px)',
            marginBottom: '1rem',
            overflow: 'hidden',
          }}
        >
          <Chart
            width="100%"
            height="100%"
            chartType="AreaChart"
            loader={
              <div className="text-center p-4">
                <div className="spinner-border text-success" role="status">
                  <span className="sr-only">
                    {i18n.language === 'gr' ? 'Φόρτωση...' : 'Loading...'}
                  </span>
                </div>
                <p className="mt-2 text-muted small">
                  {i18n.language === 'gr'
                    ? 'Φόρτωση δεδομένων γραφήματος...'
                    : 'Loading chart data...'}
                </p>
              </div>
            }
            data={chartData}
            options={options}
          />
        </div>
      ) : (
        <div
          style={{ height: 'clamp(200px, 50vh, 400px)' }}
          className="d-flex align-items-center justify-content-center"
        >
          <div className="text-center">
            <div className="spinner-border text-success" role="status">
              <span className="sr-only">
                {i18n.language === 'gr' ? 'Φόρτωση...' : 'Loading...'}
              </span>
            </div>
            <p className="mt-3 text-muted">
              {i18n.language === 'gr' ? 'Φόρτωση στατιστικών...' : 'Loading statistics...'}
            </p>
          </div>
        </div>
      )}
    </>
  );
};
